import { useEffect, useState } from 'react';
import { Modal } from '../components/Modal';
import { useProduct, useProductMutations } from './api';
import { useToast } from '../components/Toast';
import { ApiRequestError } from '../lib/api';
import { ProductPhotoManager } from './ProductPhotos';
import type { Product } from '../lib/types';

const APP_KEYS = ['displayName', 'description', 'category', 'subcategory', 'sellingPrice', 'displayPrice', 'baseQuantity', 'tags', 'status'] as const;

export function ProductEditModal({
  product,
  open,
  onClose,
  onSaved,
}: {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { update } = useProductMutations();
  const { data: live, refetch: refetchProduct } = useProduct(product?._id);
  const toast = useToast();
  const [showSource, setShowSource] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!product) return;
    setShowSource(false);
    setF({
      displayName: product.displayName ?? '',
      description: product.description ?? '',
      category: product.category ?? '',
      subcategory: product.subcategory ?? '',
      sellingPrice: product.sellingPrice ?? '',
      displayPrice: product.displayPrice ?? '',
      baseQuantity: String(product.baseQuantity ?? 0),
      tags: (product.tags ?? []).join(', '),
      status: product.status,
      photoId: product.photoId ?? '',
      size: product.size ?? '',
      grossWeight: product.grossWeight ?? '',
      stoneWeight: product.stoneWeight ?? '',
      stoneAmount: product.stoneAmount ?? '',
    });
  }, [product]);

  if (!product) return null;

  const save = async () => {
    const body: Record<string, unknown> = {
      displayName: f.displayName || undefined,
      description: f.description || undefined,
      category: f.category || undefined,
      subcategory: f.subcategory || undefined,
      sellingPrice: f.sellingPrice === '' ? null : f.sellingPrice,
      displayPrice: f.displayPrice === '' ? null : f.displayPrice,
      baseQuantity: Number(f.baseQuantity) || 0,
      tags: f.tags ? f.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      status: f.status,
    };
    if (showSource) {
      body.confirmSourceChange = true;
      body.photoId = f.photoId || null;
      body.size = f.size || null;
      body.grossWeight = f.grossWeight || undefined;
      body.stoneWeight = f.stoneWeight === '' ? null : f.stoneWeight;
      body.stoneAmount = f.stoneAmount === '' ? null : f.stoneAmount;
    }
    try {
      await update.mutateAsync({ id: product._id, body });
      toast('Product updated', 'success');
      onSaved?.();
      onClose();
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Save failed', 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${product.barcodeNo}`}
      size="lg"
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={save} disabled={update.isPending}>
            Save changes
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <L label="Display name" span>
          <input className="input" value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} />
        </L>
        <L label="Category">
          <input className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        </L>
        <L label="Subcategory">
          <input className="input" value={f.subcategory} onChange={(e) => setF({ ...f, subcategory: e.target.value })} />
        </L>
        <L label="Display price (₹)">
          <input className="input" inputMode="decimal" value={f.displayPrice} onChange={(e) => setF({ ...f, displayPrice: e.target.value })} />
        </L>
        <L label="Selling price (₹)">
          <input className="input" inputMode="decimal" value={f.sellingPrice} onChange={(e) => setF({ ...f, sellingPrice: e.target.value })} />
        </L>
        <L label="Base quantity">
          <input className="input" inputMode="numeric" value={f.baseQuantity} onChange={(e) => setF({ ...f, baseQuantity: e.target.value })} />
        </L>
        <L label="Status">
          <select className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="archived">archived</option>
          </select>
        </L>
        <L label="Tags (comma separated)" span>
          <input className="input" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} />
        </L>
        <L label="Description" span>
          <textarea className="input min-h-[64px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </L>
      </div>

      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <label className="flex items-center gap-2 text-sm font-medium text-amber-800">
          <input type="checkbox" checked={showSource} onChange={(e) => setShowSource(e.target.checked)} />
          Edit imported source fields (Barcode-linked master data)
        </label>
        {showSource && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <L label="Photo ID">
              <input className="input" value={f.photoId} onChange={(e) => setF({ ...f, photoId: e.target.value })} />
            </L>
            <L label="Size">
              <input className="input" value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} />
            </L>
            <L label="Gross weight">
              <input className="input" inputMode="decimal" value={f.grossWeight} onChange={(e) => setF({ ...f, grossWeight: e.target.value })} />
            </L>
            <L label="Stone weight">
              <input className="input" inputMode="decimal" value={f.stoneWeight} onChange={(e) => setF({ ...f, stoneWeight: e.target.value })} />
            </L>
            <L label="Stone amount">
              <input className="input" inputMode="decimal" value={f.stoneAmount} onChange={(e) => setF({ ...f, stoneAmount: e.target.value })} />
            </L>
            <p className="col-span-2 text-xs text-amber-700">
              Original Excel values are preserved for traceability — these edits only change the working
              record. Barcode No cannot be changed here.
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-ink-100 pt-4">
        <ProductPhotoManager
          product={live ?? product}
          onChanged={() => {
            refetchProduct();
            onSaved?.();
          }}
        />
      </div>
    </Modal>
  );
}

function L({ label, span, children }: { label: string; span?: boolean; children: React.ReactNode }) {
  return (
    <label className={span ? 'col-span-2 block' : 'block'}>
      <span className="label">{label}</span>
      {children}
    </label>
  );
}

export const PRODUCT_APP_KEYS = APP_KEYS;
